import React, { useEffect, useRef } from "react";
import { useAiChat } from "../../provider/AiChatProvider";
import AiChatMessage from "./AiChatMessage";
import { Box, Stack } from "@mui/material";
import {
  useAiEventStreamControls,
  useAiEventStreamEventHandler,
  useAiEventStreamLifecycle,
  useAiPromptGenerationStream,
} from "../../hooks/useAiEventStream";
import { fetchCollection, queryKeys } from "../../services/ReactQuery/reactQueryService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AiThinkingBubble from "./AiThinkingBubble";
import { AiChatMessage as AiChatMessageType } from "../../types/AIChat/AiChatMessage";
import { HydraCollectionResult } from "../../types/HydraCollectionResult";
import { StreamingMarkdownText } from "../AiEventStream/StreamingMarkdownText";
import { useCase } from "../../provider/CaseProvider";
import { AiThreadUpdated } from "../../types/AiEventStream";

export default function AiChatThreadMessages() {
  const { activeThreadId, setIsLoading } = useAiChat();
  const { product } = useCase();
  const queryClient = useQueryClient();
  const { reset } = useAiEventStreamControls();

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const aiChatMessagesUri = "ai_chat_messages";
  const aiChatMessagesFilter = { aiChatThread: activeThreadId };
  const { data: aiChatMessagesResponse } = useQuery<HydraCollectionResult<AiChatMessageType>>({
    queryKey: queryKeys.collection(aiChatMessagesUri, aiChatMessagesFilter),
    queryFn: () => fetchCollection(aiChatMessagesUri, aiChatMessagesFilter),
    enabled: !!activeThreadId
  });

  const handleThreadUpdated = async () => {
    try {
      if (!product || !activeThreadId) {
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeys.collection(aiChatMessagesUri, aiChatMessagesFilter)
      });
    } finally {
      reset();
      setIsLoading(false);
    }
  };

  useAiEventStreamEventHandler<AiThreadUpdated>("threadUpdated", handleThreadUpdated);

  const currentMessages = aiChatMessagesResponse?.["hydra:member"] || [];

  const { text } = useAiPromptGenerationStream();
  const { hasStreamStarted } = useAiEventStreamLifecycle();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
  }, [currentMessages.length, hasStreamStarted, text]);

  return (
    <>
      <Stack spacing={1}>
        {currentMessages.map((message) => (
          <AiChatMessage message={message} key={message.id} />
        ))}
        {text && <StreamingMarkdownText text={text} />}
        {hasStreamStarted && <AiThinkingBubble />}
      </Stack>
      <Box ref={messagesEndRef} />
    </>
  );
}
